import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { publicAssetUrl } from "@/lib/api";

/**
 * Swipeable hero carousel for the offer-detail page.
 *
 * `images` is a de-duplicated ordered list of URLs (poster + gallery entries).
 * When the list has 0 items, renders nothing. When it has 1 item, renders it
 * as a single image (no dots, no arrows). When >=2 items, activates Embla
 * with touch drag, dots indicator, and (on hover-capable devices) side
 * arrows.
 *
 * Preserves the surrounding aspect-ratio wrapper so overlays (discount badge,
 * heart button, countdown strip) rendered by the parent stay aligned.
 */
export default function OfferHeroCarousel({ images = [], title = "", testidPrefix = "offer-gallery" }) {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        align: "start",
        skipSnaps: false,
        containScroll: "trimSnaps",
    });
    const [index, setIndex] = useState(0);
    const [broken, setBroken] = useState(() => new Set());

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onSelect);
        return () => {
            emblaApi.off("select", onSelect);
            emblaApi.off("reInit", onSelect);
        };
    }, [emblaApi, onSelect]);

    // Filter out slides that reported a broken image; do it AFTER Embla so
    // indices stay stable during the same session.
    const usable = images.filter((u) => !broken.has(u));
    if (usable.length === 0) return null;

    const scrollTo = (i) => emblaApi?.scrollTo(i);
    const scrollPrev = () => emblaApi?.scrollPrev();
    const scrollNext = () => emblaApi?.scrollNext();

    // Single image → skip carousel wiring entirely.
    if (usable.length === 1) {
        const only = usable[0];
        return (
            <img
                src={publicAssetUrl(only)}
                alt={title}
                className="h-full w-full object-cover"
                data-testid={`${testidPrefix}-slide-0`}
                onError={() => setBroken((prev) => new Set(prev).add(only))}
            />
        );
    }

    return (
        <div className="relative h-full w-full" data-testid={`${testidPrefix}-carousel`}>
            <div className="h-full w-full overflow-hidden" ref={emblaRef}>
                <div className="flex h-full">
                    {usable.map((u, i) => (
                        <div
                            key={u}
                            className="relative h-full min-w-0 flex-[0_0_100%]"
                            data-testid={`${testidPrefix}-slide-${i}`}
                        >
                            <img
                                src={publicAssetUrl(u)}
                                alt={`${title} — image ${i + 1}`}
                                className="h-full w-full object-cover"
                                loading={i === 0 || i === index + 1 ? "eager" : "lazy"}
                                draggable={false}
                                onError={() => setBroken((prev) => new Set(prev).add(u))}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Dots */}
            <div className="pointer-events-none absolute inset-x-0 bottom-3 flex items-center justify-center gap-1.5">
                {usable.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => scrollTo(i)}
                        aria-label={`Go to image ${i + 1} of ${usable.length}`}
                        data-testid={`${testidPrefix}-dot-${i}`}
                        className={`pointer-events-auto h-2 rounded-full transition-all ${i === index ? "w-6 bg-white" : "w-2 bg-white/60"}`}
                    />
                ))}
            </div>

            {/* Slide counter — small pill top-right of the carousel area */}
            <div
                className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur-sm"
                data-testid={`${testidPrefix}-counter`}
            >
                {index + 1}/{usable.length}
            </div>

            {/* Arrows — hover-visible on desktop; hidden on coarse-pointer */}
            <button
                type="button"
                onClick={scrollPrev}
                disabled={index === 0}
                aria-label="Previous image"
                data-testid={`${testidPrefix}-arrow-prev`}
                className="absolute left-2 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/85 p-2 text-neutral-800 shadow transition hover:bg-white disabled:opacity-40 sm:inline-flex"
            >
                <ChevronLeft size={18} />
            </button>
            <button
                type="button"
                onClick={scrollNext}
                disabled={index === usable.length - 1}
                aria-label="Next image"
                data-testid={`${testidPrefix}-arrow-next`}
                className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/85 p-2 text-neutral-800 shadow transition hover:bg-white disabled:opacity-40 sm:inline-flex"
            >
                <ChevronRight size={18} />
            </button>
        </div>
    );
}
